import { useState, useEffect, useRef, useCallback } from 'react';
import { Device, Call } from '@twilio/voice-sdk';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export type CallStatus = 'idle' | 'connecting' | 'ringing' | 'connected' | 'disconnected' | 'error';

interface UseTwilioDeviceOptions {
  onCallStatusChange?: (status: CallStatus) => void;
  onError?: (error: Error) => void;
}

export function useTwilioDevice(options: UseTwilioDeviceOptions = {}) {
  const [device, setDevice] = useState<Device | null>(null);
  const [currentCall, setCurrentCall] = useState<Call | null>(null);
  const [callStatus, setCallStatus] = useState<CallStatus>('idle');
  const [isReady, setIsReady] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  
  const durationIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const { toast } = useToast();

  const updateCallStatus = useCallback((status: CallStatus) => {
    setCallStatus(status);
    options.onCallStatusChange?.(status);
  }, [options.onCallStatusChange]);

  // Initialize the Twilio Device
  const initDevice = useCallback(async () => {
    try {
      console.log('Fetching Twilio token...');
      
      const { data, error } = await supabase.functions.invoke('twilio-token');
      
      if (error) {
        throw new Error(error.message || 'Failed to get token');
      }
      
      if (!data?.token) {
        throw new Error('No token received from server');
      }

      console.log('Token received, initializing device...');

      const newDevice = new Device(data.token, {
        codecPreferences: [Call.Codec.Opus, Call.Codec.PCMU],
        logLevel: 1,
      });

      newDevice.on('registered', () => {
        console.log('Twilio device registered');
        setIsReady(true);
      });

      newDevice.on('error', (twilioError) => {
        console.error('Twilio device error:', twilioError);
        updateCallStatus('error');
        options.onError?.(new Error(twilioError.message));
        toast({
          title: 'Call Error',
          description: twilioError.message,
          variant: 'destructive',
        });
      });

      newDevice.on('incoming', (call: Call) => {
        console.log('Incoming call:', call);
        setCurrentCall(call);
        updateCallStatus('ringing');
      });

      await newDevice.register();
      setDevice(newDevice);
      
      return newDevice;
    } catch (error: any) {
      console.error('Failed to initialize Twilio device:', error);
      options.onError?.(error);
      toast({
        title: 'Failed to initialize calling',
        description: error.message,
        variant: 'destructive',
      });
      throw error;
    }
  }, [options.onError, toast, updateCallStatus]);

  // Make an outbound call
  const makeCall = useCallback(async (
    phoneNumber: string,
    params?: { candidateId?: string; jobId?: string; record?: boolean }
  ) => {
    try {
      let activeDevice = device;
      
      if (!activeDevice || !isReady) {
        console.log('Device not ready, initializing...');
        activeDevice = await initDevice();
      }

      if (!activeDevice) {
        throw new Error('Failed to initialize calling device');
      }

      console.log('Making call to:', phoneNumber);
      updateCallStatus('connecting');

      const call = await activeDevice.connect({
        params: {
          To: phoneNumber,
          candidateId: params?.candidateId || '',
          jobId: params?.jobId || '',
          record: params?.record !== false ? 'true' : 'false',
        },
      });

      call.on('ringing', () => {
        console.log('Call is ringing');
        updateCallStatus('ringing');
      });

      call.on('accept', () => {
        console.log('Call accepted');
        updateCallStatus('connected');
        setCallDuration(0);
        
        // Start duration timer
        durationIntervalRef.current = setInterval(() => {
          setCallDuration(prev => prev + 1);
        }, 1000);
      });

      call.on('disconnect', () => {
        console.log('Call disconnected');
        updateCallStatus('disconnected');
        setCurrentCall(null);
        
        if (durationIntervalRef.current) {
          clearInterval(durationIntervalRef.current);
          durationIntervalRef.current = null;
        }
      });

      call.on('cancel', () => {
        console.log('Call cancelled');
        updateCallStatus('idle');
        setCurrentCall(null);
      });

      call.on('error', (error) => {
        console.error('Call error:', error);
        updateCallStatus('error');
        options.onError?.(error);
      });

      setCurrentCall(call);
      return call;
    } catch (error: any) {
      console.error('Failed to make call:', error);
      updateCallStatus('error');
      options.onError?.(error);
      throw error;
    }
  }, [device, isReady, initDevice, updateCallStatus, options.onError]);

  // Hang up the current call
  const hangUp = useCallback(() => {
    if (currentCall) {
      console.log('Hanging up call');
      currentCall.disconnect();
      setCurrentCall(null);
      updateCallStatus('idle');
      
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);
        durationIntervalRef.current = null;
      }
    }
  }, [currentCall, updateCallStatus]);

  // Toggle mute
  const toggleMute = useCallback(() => {
    if (currentCall) {
      const newMuteState = !isMuted;
      currentCall.mute(newMuteState);
      setIsMuted(newMuteState);
    }
  }, [currentCall, isMuted]);

  // Send DTMF digits
  const sendDigits = useCallback((digits: string) => {
    if (currentCall) {
      currentCall.sendDigits(digits);
    }
  }, [currentCall]);

  // Format duration for display
  const formatDuration = useCallback((seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (device) {
        device.destroy();
      }
      if (durationIntervalRef.current) {
        clearInterval(durationIntervalRef.current);
      }
    };
  }, [device]);

  return {
    device,
    currentCall,
    callStatus,
    isReady,
    isMuted,
    callDuration,
    formattedDuration: formatDuration(callDuration),
    initDevice,
    makeCall,
    hangUp,
    toggleMute,
    sendDigits,
  };
}
